import { useState } from "react";
import { PauseIcon, PlayIcon } from "lucide-react";

import { Column, OwnerAvatars, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useGetShortlistCandidaciesSegmentedByOwnership } from "@/hooks/queries/useGetShortlistCandidaciesSegmentedByOwnership";
import { useSetShortlistCandidaciesAgentActive } from "@/hooks/sets/useSetShortlistCandidaciesAgentActive";
import { useUpdateSetJobInternalConfig } from "@/hooks/updateSets/useUpdateSetJobInternalConfig";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { JobInternalConfig } from "@/types";

enum ResumeMode {
  ALL = "all",
  ME = "me",
}

const selectedSegment = "bg-white text-slate-900 shadow-sm hover:bg-white";
const unselectedSegment =
  "bg-transparent text-slate-500 hover:bg-transparent hover:text-slate-900";

export const StafferAgentPausePopover = ({
  jobInternalConfig,
}: {
  jobInternalConfig: JobInternalConfig;
}) => {
  /* React Hooks */
  const [open, setOpen] = useState(false);

  /* Derived */
  const isActive = jobInternalConfig.stafferAgentActive;
  const triggerLabel = isActive
    ? "AI scheduling on"
    : "AI scheduling off indefinitely";

  /* Handlers */
  const handleClose = () => {
    setOpen(false);
  };

  /* Render */
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider delayDuration={500}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                className="h-7 gap-1.5 rounded-full px-2.5 text-xs font-medium"
                type="button"
                variant="ghost"
              >
                {isActive ? (
                  <PlayIcon className="h-3.5 w-3.5 shrink-0 fill-emerald-600 text-emerald-600" />
                ) : (
                  <PauseIcon className="h-3.5 w-3.5 shrink-0 fill-slate-500 text-slate-500" />
                )}
                <span>{triggerLabel}</span>
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          {!isActive && (
            <TooltipContent side="bottom" className="text-xs">
              Paused indefinitely.
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <PopoverContent align="end" className="w-[360px] p-3.5">
        {isActive ? (
          <PauseSection
            jobInternalConfig={jobInternalConfig}
            onClose={handleClose}
          />
        ) : (
          <ResumeSection
            jobInternalConfig={jobInternalConfig}
            onClose={handleClose}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};

const pluralize = (count: number, singular: string, plural: string): string =>
  `${String(count)} ${count === 1 ? singular : plural}`;

const PauseSection = ({
  jobInternalConfig,
  onClose,
}: {
  jobInternalConfig: JobInternalConfig;
  onClose: () => void;
}) => {
  /* React Hooks */
  const { mutate: updateJobInternalConfig, isPending } =
    useUpdateSetJobInternalConfig();

  /* Handlers */
  const handlePause = () => {
    mixpanelUtils.trackButtonClick(
      "Pause AI Scheduling Agent",
      MIXPANEL_SCREENS.JOB,
      {
        job_id: jobInternalConfig.jobId,
        job_internal_config_id: jobInternalConfig.id,
      },
    );
    updateJobInternalConfig(
      {
        id: jobInternalConfig.id,
        updateRequest: { stafferAgentActive: false },
      },
      { onSuccess: onClose },
    );
  };

  /* Render */
  return (
    <Column className="gap-3.5">
      <Text className="text-xs leading-relaxed text-slate-600">
        Pause the AI scheduling agent indefinitely. Any candidacy moved to
        shortlist will remain there until a staffer manually outreaches or the
        AI scheduling is re-enabled.
      </Text>
      <Button
        className="h-9 w-full bg-rose-600 text-xs text-white hover:bg-rose-700"
        disabled={isPending}
        onClick={handlePause}
        type="button"
      >
        Pause
      </Button>
    </Column>
  );
};

const ResumeSection = ({
  jobInternalConfig,
  onClose,
}: {
  jobInternalConfig: JobInternalConfig;
  onClose: () => void;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: counts } = useGetShortlistCandidaciesSegmentedByOwnership();
  // Update
  const { mutate: updateJobInternalConfig, isPending } =
    useUpdateSetJobInternalConfig();
  const setShortlistCandidaciesAgentActive =
    useSetShortlistCandidaciesAgentActive();

  /* Context */
  const { userInitialsMap } = useTaggableUsers();

  /* React Hooks */
  const [mode, setMode] = useState<ResumeMode>(ResumeMode.ME);

  /* Derived */
  const isMe = mode === ResumeMode.ME;
  const currentUserId = counts?.currentUserId;
  const staffers = counts?.staffers ?? [];
  const unownedCount = counts?.unownedCount ?? 0;
  const myOwnedCount =
    staffers.find((staffer) => staffer.userId === currentUserId)?.ownedCount ??
    0;
  const otherStaffers = staffers.filter(
    (staffer) => staffer.userId !== currentUserId,
  );
  const otherOwnedCount = otherStaffers.reduce(
    (sum, staffer) => sum + staffer.ownedCount,
    0,
  );

  // Who gets contacted vs. who stays put, per mode.
  const contactedCount = isMe ? myOwnedCount : myOwnedCount + otherOwnedCount;
  const skippedCount = isMe ? otherOwnedCount + unownedCount : unownedCount;
  const avatarUserIds = (isMe ? otherStaffers : staffers).map(
    (staffer) => staffer.userId,
  );

  /* Handlers */
  const handleResume = () => {
    mixpanelUtils.trackButtonClick(
      "Resume AI Scheduling Agent",
      MIXPANEL_SCREENS.JOB,
      {
        job_id: jobInternalConfig.jobId,
        job_internal_config_id: jobInternalConfig.id,
        resume_mode: mode,
        contacted_count: contactedCount,
        skipped_count: skippedCount,
      },
    );
    updateJobInternalConfig(
      {
        id: jobInternalConfig.id,
        updateRequest: {
          stafferAgentActive: true,
          ...(isMe && currentUserId != null
            ? { resumeForOwnerUserId: currentUserId }
            : {}),
        },
      },
      {
        onSuccess: () => {
          setShortlistCandidaciesAgentActive(isMe ? currentUserId : undefined);
          onClose();
        },
      },
    );
  };

  /* Render */
  // Bucket each ownership group into contacted vs. not, per the resume mode.
  // Yours are always contacted; unassigned never are; others depend on mode.
  const contactedLines: string[] = [];
  const notContactedLines: string[] = [];
  if (myOwnedCount > 0) {
    contactedLines.push(
      pluralize(myOwnedCount, "candidacy you own", "candidacies you own"),
    );
  }
  if (otherOwnedCount > 0) {
    const line = pluralize(
      otherOwnedCount,
      "candidacy owned by other staffers",
      "candidacies owned by other staffers",
    );
    (isMe ? notContactedLines : contactedLines).push(line);
  }
  if (unownedCount > 0) {
    notContactedLines.push(
      pluralize(unownedCount, "unassigned candidacy", "unassigned candidacies"),
    );
  }

  return (
    <Column className="gap-3.5">
      <Row className="rounded-md bg-slate-100 p-0.5">
        <Button
          className={cn(
            "h-7 flex-1 text-xs font-medium",
            isMe
              ? cn(selectedSegment, "text-emerald-700 hover:text-emerald-700")
              : unselectedSegment,
          )}
          onClick={() => {
            setMode(ResumeMode.ME);
          }}
          type="button"
          variant="ghost"
        >
          Resume for me
        </Button>
        <Button
          className={cn(
            "h-7 flex-1 text-xs font-medium",
            !isMe
              ? cn(selectedSegment, "text-emerald-700 hover:text-emerald-700")
              : unselectedSegment,
          )}
          onClick={() => {
            setMode(ResumeMode.ALL);
          }}
          type="button"
          variant="ghost"
        >
          Resume for all
        </Button>
      </Row>

      {avatarUserIds.length > 0 && (
        <OwnerAvatars
          ownerUserIds={avatarUserIds}
          userInitialsMap={userInitialsMap}
        />
      )}

      <Column className="gap-1">
        <Text className="text-xs font-medium text-slate-700">
          To be contacted by AI scheduling agent:
        </Text>
        {contactedLines.length === 0 ? (
          <Text className="text-xs italic leading-relaxed text-slate-500">
            • None
          </Text>
        ) : (
          contactedLines.map((line) => (
            <Text key={line} className="text-xs leading-relaxed text-slate-600">
              • {line}
            </Text>
          ))
        )}
      </Column>
      <Column className="gap-1">
        <Text className="text-xs font-medium text-slate-700">
          Will not be contacted:
        </Text>
        {notContactedLines.length === 0 ? (
          <Text className="text-xs italic leading-relaxed text-slate-500">
            • None
          </Text>
        ) : (
          notContactedLines.map((line) => (
            <Text key={line} className="text-xs leading-relaxed text-slate-600">
              • {line}
            </Text>
          ))
        )}
      </Column>

      {!isMe && otherOwnedCount > 0 && (
        <Text className="text-xs leading-relaxed text-amber-700">
          • Ensure other staffers are aware before you unleash their AI
          scheduling agent.
        </Text>
      )}

      <Button
        className="h-9 w-full bg-emerald-600 text-xs text-white hover:bg-emerald-700"
        disabled={isPending || !counts}
        onClick={handleResume}
        type="button"
      >
        Resume
      </Button>
    </Column>
  );
};
